import { useState, useEffect } from "react";
import { Wifi, WifiOff, LogOut, Download, FileText, ShieldCheck, Gauge, Router, KeyRound, CircleDollarSign, Info, Loader2, TriangleAlert, Plus, Send, MessageSquare, ArrowUpCircle, CheckCircle2, FileSignature } from "lucide-react";
import SpeedTest from "@/components/SpeedTest";
import SpeedHistory from "@/components/SpeedHistory";

const BASE = process.env.REACT_APP_BACKEND_URL;

function urlB64ToUint8(b) {
  const pad = "=".repeat((4 - (b.length % 4)) % 4);
  const raw = atob((b + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function Row({ label, value, mono, testid }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-800 text-sm" data-testid={testid}>
      <span className="text-slate-400">{label}</span>
      <span className={`text-slate-100 text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [hidden, setHidden] = useState(false);
  const standalone = typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  useEffect(() => {
    const m = document.querySelector("link[rel=manifest]");
    if (m) m.setAttribute("href", "/manifest-klient.json");
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  if (standalone || hidden) return null;
  if (!prompt && !isIOS) return null;
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const res = await prompt.userChoice;
    if (res.outcome === "accepted") setHidden(true);
    setPrompt(null);
  };
  return (
    <div className="bg-cyan-600/15 border-b border-cyan-500/40 px-4 py-2.5 flex items-center gap-3" data-testid="client-pwa-install-banner">
      <Download size={18} className="text-cyan-300 shrink-0" />
      {prompt ? (
        <>
          <span className="text-sm text-cyan-100 flex-1">Zainstaluj aplikację na telefonie, aby mieć szybki dostęp i powiadomienia.</span>
          <button onClick={install} data-testid="client-pwa-install-btn" className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap">Zainstaluj</button>
        </>
      ) : (
        <span className="text-sm text-cyan-100 flex-1">Aby zainstalować na iPhone: przycisk <b>Udostępnij</b> → <b>Dodaj do ekranu głównego</b>.</span>
      )}
    </div>
  );
}

export default function ClientPortal() {
  const [token, setToken] = useState(localStorage.getItem("portal_token") || "");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [sreqs, setSreqs] = useState([]);
  const [annexes, setAnnexes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tForm, setTForm] = useState({ title: "", description: "", priority: "średni" });
  const [sForm, setSForm] = useState({ requested_plan: "", note: "", pin: "" });
  const [msg, setMsg] = useState("");

  const authHdr = () => ({ Authorization: `Bearer ${token}` });
  const loadExtra = async (tok) => {
    const h = { headers: { Authorization: `Bearer ${tok}` } };
    try { setTickets(await (await fetch(`${BASE}/api/portal/tickets`, h)).json()); } catch { /* ignore */ }
    try { setSreqs(await (await fetch(`${BASE}/api/portal/service-requests`, h)).json()); } catch { /* ignore */ }
    try { setAnnexes(await (await fetch(`${BASE}/api/portal/annexes`, h)).json()); } catch { /* ignore */ }
    try { setInvoices(await (await fetch(`${BASE}/api/portal/invoices`, h)).json()); } catch { /* ignore */ }
    try { const p = await (await fetch(`${BASE}/api/portal/plans`, h)).json(); setPlans(p.plans || []); } catch { /* ignore */ }
  };

  const loadMe = async (tok) => {
    try {
      const r = await fetch(`${BASE}/api/portal/me`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error();
      setMe(await r.json());
      loadExtra(tok);
    } catch { logout(); }
  };
  useEffect(() => { if (token) loadMe(token); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
        const reg = await navigator.serviceWorker.register("/sw.js");
        if ((await Notification.requestPermission()) !== "granted") return;
        const { public_key } = await (await fetch(`${BASE}/api/portal/push/key`)).json();
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(public_key) });
        await fetch(`${BASE}/api/portal/push/subscribe`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ subscription: sub }) });
      } catch { /* ignore */ }
    })();
  }, [token]);

  const login = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await fetch(`${BASE}/api/portal/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, pin }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Błąd logowania");
      localStorage.setItem("portal_token", d.token); setToken(d.token); await loadMe(d.token);
    } catch (e2) { setErr(e2.message || "Błąd logowania"); }
    finally { setBusy(false); }
  };
  const logout = () => { localStorage.removeItem("portal_token"); setToken(""); setMe(null); };

  const download = async (kind) => {
    try {
      const r = await fetch(`${BASE}/api/portal/${kind}?auth=${token}`, { headers: { Authorization: `Bearer ${token}` } });
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const d = await r.json();
        if (d.redirect) { window.open(d.redirect, "_blank"); return; }
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${kind}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const submitTicket = async (e) => {
    e.preventDefault(); setMsg("");
    const r = await fetch(`${BASE}/api/portal/tickets`, { method: "POST", headers: { "Content-Type": "application/json", ...authHdr() }, body: JSON.stringify(tForm) });
    if (r.ok) { setTForm({ title: "", description: "", priority: "średni" }); setMsg("Zgłoszenie wysłane — dziękujemy."); loadExtra(token); }
    else setMsg("Nie udało się wysłać zgłoszenia.");
  };
  const downloadIptv = async () => {
    try {
      const r = await fetch(`${BASE}/api/portal/iptv-pdf?auth=${token}`, { headers: authHdr() });
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("application/json")) { const d = await r.json(); if (d.redirect) { window.open(d.redirect, "_blank"); return; } }
      const blob = await r.blob(); window.open(URL.createObjectURL(blob), "_blank");
    } catch { /* ignore */ }
  };
  const submitSreq = async (e) => {
    e.preventDefault(); setMsg("");
    const r = await fetch(`${BASE}/api/portal/service-request`, { method: "POST", headers: { "Content-Type": "application/json", ...authHdr() }, body: JSON.stringify(sForm) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { setSForm({ requested_plan: "", note: "", pin: "" }); setMsg("Wniosek o zmianę usługi wysłany."); loadExtra(token); }
    else setMsg(d.detail || "Nie udało się wysłać wniosku.");
  };
  const acceptAnnex = async (a) => {
    const pin = window.prompt(`Podaj kod PIN, aby zaakceptować aneks ${a.annex_no}`);
    if (!pin) return;
    const r = await fetch(`${BASE}/api/portal/annexes/${a.id}/accept`, { method: "POST", headers: { "Content-Type": "application/json", ...authHdr() }, body: JSON.stringify({ pin }) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { setMsg("Aneks zaakceptowany."); loadExtra(token); }
    else setMsg(d.detail || "Nie udało się zaakceptować aneksu.");
  };
  const dlAnnex = async (a) => {
    const r = await fetch(`${BASE}/api/portal/annexes/${a.id}/pdf?auth=${token}`, { headers: authHdr() });
    const blob = await r.blob(); const url = URL.createObjectURL(blob); window.open(url, "_blank");
  };

  if (!token || !me) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="portal-login-screen">
        <InstallBanner />
        <div className="flex-1 flex items-center justify-center p-4">
        <form onSubmit={login} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4" data-testid="portal-login">
          <div className="text-center">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-cyan-600/20 items-center justify-center mb-2"><Wifi className="text-cyan-400" size={28} /></div>
            <h1 className="text-xl font-heading font-bold text-white">Panel Klienta</h1>
            <p className="text-xs text-slate-400 mt-1">Zaloguj się e-mailem i kodem PIN od operatora</p>
          </div>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" data-testid="portal-email" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
          <input inputMode="numeric" required value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Kod PIN" data-testid="portal-pin" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono tracking-widest text-center" />
          {err && <p className="text-red-400 text-xs text-center" data-testid="portal-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="portal-submit" className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Zaloguj
          </button>
        </form>
        </div>
      </div>
    );
  }

  const online = me.online;
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-12" data-testid="portal-dashboard">
      <InstallBanner />
      <header className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-bold"><Wifi size={18} className="text-cyan-400" /> Panel Klienta</div>
        <button onClick={logout} data-testid="portal-logout" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm"><LogOut size={16} /> Wyloguj</button>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5 space-y-4">
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${online === false ? "border-red-700/50 bg-red-950/30" : online ? "border-emerald-700/50 bg-emerald-950/30" : "border-slate-700 bg-slate-900/50"}`} data-testid="portal-status">
          {online === false ? <WifiOff className="text-red-400" /> : <Wifi className={online ? "text-emerald-400" : "text-slate-400"} />}
          <div>
            <div className="font-heading font-bold">{online === false ? "Usługa nieaktywna / ONU offline" : online ? "Usługa działa — ONU online" : "Status ONU nieznany"}</div>
            <div className="text-xs text-slate-400">{me.name} · {me.status || "-"}{me.onu_status ? ` · ONU: ${me.onu_status}` : ""}</div>
          </div>
        </div>

        {me.active_faults && me.active_faults.length > 0 ? (
          <div className="rounded-xl border border-amber-700/50 bg-amber-950/30 p-4" data-testid="portal-fault-status">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2 text-amber-300"><TriangleAlert size={15} /> Awaria w Twojej okolicy</h2>
            <div className="space-y-2">
              {me.active_faults.map((f, i) => (
                <div key={i} className="text-xs text-slate-200 border-t border-amber-800/40 pt-2 first:border-t-0 first:pt-0" data-testid={`portal-fault-${i}`}>
                  <div className="font-semibold text-amber-200">{f.title}</div>
                  <div className="text-slate-400">Status: <b className="text-slate-200">{f.status}</b>{f.area ? <> · Obszar: <b className="text-slate-200">{f.area}</b></> : null}</div>
                  {f.eta ? <div className="text-slate-400">Planowane usunięcie: <b className="text-emerald-300" data-testid={`portal-fault-eta-${i}`}>{f.eta.replace("T", " ")}</b></div> : <div className="text-slate-500">Trwają prace nad usunięciem.</div>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-700/50 bg-emerald-950/30 p-4 flex items-center gap-3" data-testid="portal-fault-status">
            <CheckCircle2 className="text-emerald-400" size={18} />
            <div className="text-sm text-slate-200">Brak znanych awarii w Twojej okolicy.</div>
          </div>
        )}

        <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
          <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><Info size={15} className="text-cyan-400" /> Parametry usługi</h2>
          <Row label="Adres" value={me.address} testid="portal-address" />
          <Row label="Numer umowy" value={me.contract_no} mono />
          <Row label="Plan" value={me.speed_plan} testid="portal-plan" />
          <Row label="Prędkość" value={(me.download_mbps || me.upload_mbps) ? `${me.download_mbps || "?"} / ${me.upload_mbps || "?"} Mb/s` : null} mono />
          <Row label="Typ połączenia" value={me.connection_type} />
          <Row label="Adres IP" value={me.ip_address} mono />
        </section>

        {(me.pppoe_login || me.pppoe_password) && (
          <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><KeyRound size={15} className="text-amber-400" /> Dane PPPoE</h2>
            <Row label="Login" value={me.pppoe_login} mono testid="portal-pppoe-login" />
            <Row label="Hasło" value={me.pppoe_password} mono testid="portal-pppoe-pass" />
          </section>
        )}

        {me.bank_account && (
          <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
            <h2 className="font-heading font-bold text-sm mb-1 flex items-center gap-2"><CircleDollarSign size={15} className="text-emerald-400" /> Płatności</h2>
            <Row label="Nr konta do wpłat" value={me.bank_account} mono />
          </section>
        )}

        {invoices.length > 0 && (
          <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4" data-testid="portal-invoices">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><FileText size={15} className="text-cyan-400" /> Moje faktury</h2>
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/50 p-3" data-testid={`portal-invoice-${inv.id}`}>
                  <div>
                    <div className="font-mono text-sm text-white">{inv.invoice_no}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{inv.issue_date} · brutto <b className="text-cyan-300">{Number(inv.gross).toFixed(2)} zł</b></div>
                  </div>
                  <button onClick={() => window.open(`${BASE}/api/portal/invoices/${inv.id}/pdf?auth=${token}`, "_blank")} data-testid={`portal-invoice-pdf-${inv.id}`} className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-3 py-2 flex items-center gap-1"><Download size={14} /> PDF</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {(me.iptv_plan || me.voip_tariff || me.mobile_plan) && (
          <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4" data-testid="portal-services">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><Info size={15} className="text-cyan-400" /> Dodatkowe usługi</h2>
            <Row label="Pakiet IPTV" value={me.iptv_plan} testid="portal-iptv-plan" />
            <Row label="Taryfa VoIP" value={me.voip_tariff} />
            <Row label="Plan komórkowy" value={me.mobile_plan} />
            {me.voip_tariff_detail && <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans mt-1">{me.voip_tariff_detail}</pre>}
            {me.mobile_plan_detail && <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans mt-1">{me.mobile_plan_detail}</pre>}
            {me.iptv_plan && me.has_iptv_pdf && (
              <button onClick={downloadIptv} data-testid="portal-iptv-pdf" className="mt-2 w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg py-2.5 text-sm"><FileText size={16} /> Lista kanałów IPTV (PDF)</button>
            )}
          </section>
        )}

        <section>
          <SpeedTest saveToken={token} savePortal />
          {me.last_speedtest && (
            <p className="text-[11px] text-slate-500 mt-1">Ostatni pomiar: {me.last_speedtest.download_mbps}/{me.last_speedtest.upload_mbps} Mb/s · ping {me.last_speedtest.ping_ms} ms</p>
          )}
          <SpeedHistory endpoint="/portal/speedtests" portalToken={token} />
        </section>

        <section className="grid grid-cols-2 gap-2">
          <button onClick={() => download("contract")} data-testid="portal-contract" className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg py-2.5 text-sm"><FileText size={16} /> Umowa (PDF)</button>
          <button onClick={() => download("rodo")} data-testid="portal-rodo" className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg py-2.5 text-sm"><ShieldCheck size={16} /> Zgoda RODO (PDF)</button>
        </section>

        {msg && <div className="text-center text-xs text-cyan-300 bg-cyan-950/40 border border-cyan-800 rounded-lg py-2" data-testid="portal-msg">{msg}</div>}

        {annexes.length > 0 && (
          <section className="rounded-xl border border-amber-700/40 bg-amber-950/10 p-4" data-testid="portal-annexes">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><FileSignature size={15} className="text-amber-400" /> Aneksy do umowy</h2>
            <div className="space-y-2">
              {annexes.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-700 bg-slate-900/50 p-3" data-testid={`portal-annex-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm text-white">{a.annex_no}</div>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">{a.status}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{a.from_plan || "-"} → <span className="text-cyan-300">{a.to_plan}</span> {(a.to_download_mbps || a.to_upload_mbps) ? `(${a.to_download_mbps || "?"}/${a.to_upload_mbps || "?"} Mb/s)` : ""}</div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => dlAnnex(a)} data-testid={`portal-annex-pdf-${a.id}`} className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-3 py-1.5 flex items-center gap-1"><Download size={13} /> PDF</button>
                    {(a.status === "wysłany" || a.status === "wyslany") && (
                      <button onClick={() => acceptAnnex(a)} data-testid={`portal-annex-accept-${a.id}`} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded px-3 py-1.5 flex items-center gap-1"><CheckCircle2 size={13} /> Akceptuję (PIN)</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4" data-testid="portal-service-change">
          <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><ArrowUpCircle size={15} className="text-cyan-400" /> Zmiana usługi</h2>
          <form onSubmit={submitSreq} className="space-y-2">
            <select required value={sForm.requested_plan} onChange={(e) => setSForm({ ...sForm, requested_plan: e.target.value })} data-testid="portal-sreq-plan" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white">
              <option value="">— wybierz nowy plan —</option>
              {plans.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <textarea placeholder="Uwagi (opcjonalnie)" value={sForm.note} onChange={(e) => setSForm({ ...sForm, note: e.target.value })} rows={2} data-testid="portal-sreq-note" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
            <input inputMode="numeric" required placeholder="Potwierdź kodem PIN" value={sForm.pin} onChange={(e) => setSForm({ ...sForm, pin: e.target.value })} data-testid="portal-sreq-pin" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white font-mono tracking-widest text-center" />
            <button type="submit" data-testid="portal-sreq-submit" className="w-full bg-cyan-600 hover:bg-cyan-500 text-white rounded py-2 text-sm flex items-center justify-center gap-2"><Send size={15} /> Wyślij wniosek</button>
          </form>
          {sreqs.length > 0 && (
            <div className="mt-3 space-y-1">
              {sreqs.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs border-b border-slate-800 py-1" data-testid={`portal-sreq-${r.id}`}>
                  <span className="text-slate-300">{r.requested_plan}</span>
                  <span className="text-slate-500">{r.status} · {(r.at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4" data-testid="portal-tickets">
          <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><MessageSquare size={15} className="text-cyan-400" /> Moje zgłoszenia</h2>
          <form onSubmit={submitTicket} className="space-y-2">
            <input required placeholder="Temat (np. brak internetu)" value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} data-testid="portal-ticket-title" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
            <textarea placeholder="Opis problemu" value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} rows={2} data-testid="portal-ticket-desc" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
            <div className="flex gap-2">
              <select value={tForm.priority} onChange={(e) => setTForm({ ...tForm, priority: e.target.value })} data-testid="portal-ticket-priority" className="bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white">
                {["niski", "średni", "wysoki", "krytyczny"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <button type="submit" data-testid="portal-ticket-submit" className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded py-2 text-sm flex items-center justify-center gap-2"><Plus size={15} /> Zgłoś</button>
            </div>
          </form>
          {tickets.length > 0 && (
            <div className="mt-3 space-y-1">
              {tickets.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs border-b border-slate-800 py-1.5" data-testid={`portal-ticket-${t.id}`}>
                  <span className="text-slate-200">{t.title}</span>
                  <span className="text-slate-500">{t.status} · {(t.reported_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {me.router_instructions && (
          <section className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
            <h2 className="font-heading font-bold text-sm mb-2 flex items-center gap-2"><Router size={15} className="text-cyan-400" /> Jak skonfigurować router i WiFi</h2>
            <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{me.router_instructions}</pre>
          </section>
        )}
        {me.company_website && (
          <a href={me.company_website.startsWith("http") ? me.company_website : `https://${me.company_website}`} target="_blank" rel="noopener noreferrer" data-testid="portal-website" className="block text-center text-sm text-cyan-400 hover:text-cyan-300 underline py-2">
            Strona operatora
          </a>
        )}
      </main>
    </div>
  );
}
